import React from 'react';
import { 
  JobCategoryPill,
  FlipIndicator,
  CustomJobIndicator,
  DateOverrideIndicator,
  OwnerArrivalIndicator,
  OwnerDepartureIndicator,
  MaintenanceStartIndicator,
  MaintenanceEndingIndicator,
  LateCheckoutIndicator,
  EarlyCheckinIndicator,
  CustomFlagIndicator
} from '../components/job-indicators';
// Removed ReservationTypeBadge import - now using specific maintenance indicators instead
import { isManualJob, isLateCheckout, isEarlyCheckin } from './job-utils';

/**
 * Helper functions to check job properties - using GraphQL resolver data
 */
export const isOwnerArrival = (job) => {
  return job.isOwnerArrival === true;
};

export const isOwnerDeparture = (job) => {
  return job.isOwnerDeparture === true;
};

/**
 * Helper functions to check maintenance properties - using GraphQL resolver data
 */
export const isMaintenanceStart = (job) => {
  return job.isMaintenanceStart === true;
};

export const isMaintenanceEnding = (job) => {
  return job.isMaintenanceEnding === true;
};

/**
 * Centralized function to render all job badges consistently
 * Enhanced for real-time updates - reduced memoization for better responsiveness
 * @param {Object} job - The job object
 * @param {Object} nextReservationData - Optional next reservation data for early checkin detection
 * @param {Object} options - Optional configuration
 * @returns {JSX.Element[]} Array of badge components
 */
export const renderJobBadges = (job, nextReservationData = null, options = {}) => {
  const badges = [];
  
  // Generate a unique key based on job ID and timestamp to prevent stale renders
  const keyPrefix = `${job.id}-${Date.now()}`;

  // Service Type Category Pill (always first)
  badges.push(
    <JobCategoryPill 
      key={`${keyPrefix}-category`}
      category={job.category} 
      serviceType={job.serviceTypeId || job.serviceType} 
      cleaningType={job.cleaningType} 
    />
  );

  // Flip indicator
  if (job.isFlip) {
    badges.push(<FlipIndicator key={`${keyPrefix}-flip`} job={job} />);
  }

  // Manual/Custom job indicator
  if (isManualJob(job)) {
    badges.push(<CustomJobIndicator key={`${keyPrefix}-custom`} />);
  }

  // Date override indicator - exclude for Pre-arrival Inspection jobs
  if (job.hasDateOverride && !(job.category === 'INSPECTION' && job.serviceType?.title?.toLowerCase().includes('pre-arrival'))) {
    badges.push(<DateOverrideIndicator key={`${keyPrefix}-date-override`} />);
  }



  // Late checkout indicator - always recalculate for real-time updates
  if (isLateCheckout(job)) {
    badges.push(<LateCheckoutIndicator key={`${keyPrefix}-late-checkout`} />);
  }

  // Early check-in indicator - always recalculate for real-time updates
  // Pass nextReservationData if available for better accuracy
  if (isEarlyCheckin(job, nextReservationData)) {
    badges.push(<EarlyCheckinIndicator key={`${keyPrefix}-early-checkin`} />);
  }

  // Owner arrival indicator
  if (isOwnerArrival(job)) {
    badges.push(<OwnerArrivalIndicator key={`${keyPrefix}-owner-arrival`} />);
  }

  // Owner departure indicator
  if (isOwnerDeparture(job)) {
    badges.push(<OwnerDepartureIndicator key={`${keyPrefix}-owner-departure`} />);
  }

  // Maintenance start indicator
  if (isMaintenanceStart(job)) {
    badges.push(<MaintenanceStartIndicator key={`${keyPrefix}-maintenance-start`} />);
  }

  // Maintenance ending indicator
  if (isMaintenanceEnding(job)) {
    badges.push(<MaintenanceEndingIndicator key={`${keyPrefix}-maintenance-ending`} />);
  }

  // Note: Removed generic ReservationTypeBadge - now using specific maintenance indicators instead

  return badges;
};

/**
 * Enhanced version that forces real-time evaluation of indicators
 * Use this when you need immediate updates after reservation changes
 */
export const renderJobBadgesRealTime = (job, nextReservationData = null, options = {}) => {
  // Use timestamp in key to force re-render
  const timestamp = Date.now();
  return renderJobBadges(job, nextReservationData, { ...options, forceUpdate: timestamp });
};

/**
 * Helper function to render job badges in a container
 * Enhanced for real-time updates
 */
export const renderJobBadgesContainer = (job, nextReservationData = null, containerClass = 'flex flex-wrap gap-2') => {
  const badges = renderJobBadges(job, nextReservationData);
  
  if (badges.length === 0) return null;
  
  return (
    <div className={containerClass}>
      {badges}
    </div>
  );
};

/**
 * Render reservation and job-related badges for components like jobMeta
 * Enhanced for real-time updates
 * @param {Object} job - The job object
 * @param {Object} nextReservationData - Optional next reservation data
 * @param {Array} flagTemplates - Optional array of flag templates for lookup
 * @returns {JSX.Element[]} Array of reservation and job badge components
 */
export const renderReservationBadges = (job, nextReservationData = null, flagTemplates = []) => {
  const badges = [];
  
  // Generate unique keys for real-time updates
  const keyPrefix = `${job.id}-${Date.now()}`;

  // Note: Removed generic ReservationTypeBadge - now using specific maintenance indicators instead

  // Owner arrival indicator
  if (isOwnerArrival(job)) {
    badges.push(<OwnerArrivalIndicator key={`${keyPrefix}-owner-arrival`} />);
  }

  // Owner departure indicator
  if (isOwnerDeparture(job)) {
    badges.push(<OwnerDepartureIndicator key={`${keyPrefix}-owner-departure`} />);
  }

  // Maintenance start indicator
  if (isMaintenanceStart(job)) {
    badges.push(<MaintenanceStartIndicator key={`${keyPrefix}-maintenance-start`} />);
  }

  // Maintenance ending indicator
  if (isMaintenanceEnding(job)) {
    badges.push(<MaintenanceEndingIndicator key={`${keyPrefix}-maintenance-ending`} />);
  }

  // Job-related badges (exclude service type category pill as requested)
  
  // Flip indicator
  if (job.isFlip) {
    badges.push(<FlipIndicator key={`${keyPrefix}-flip`} job={job} />);
  }

  // Date override indicator - exclude for Pre-arrival Inspection jobs
  if (job.hasDateOverride && !(job.category === 'INSPECTION' && job.serviceType?.title?.toLowerCase().includes('pre-arrival'))) {
    badges.push(<DateOverrideIndicator key={`${keyPrefix}-date-override`} />);
  }



  // Late checkout indicator - always recalculate for real-time updates
  if (isLateCheckout(job)) {
    badges.push(<LateCheckoutIndicator key={`${keyPrefix}-late-checkout`} />);
  }

  // Early check-in indicator - always recalculate for real-time updates
  if (isEarlyCheckin(job, nextReservationData)) {
    badges.push(<EarlyCheckinIndicator key={`${keyPrefix}-early-checkin`} />);
  }

  // Manual/Custom job indicator
  if (isManualJob(job)) {
    badges.push(<CustomJobIndicator key={`${keyPrefix}-custom`} />);
  }

  // Custom flags - add them to the reservation badges section
  if (job.customFlags && job.customFlags.length > 0) {
    job.customFlags.forEach((flag) => {
      badges.push(
        <CustomFlagIndicator 
          key={`${keyPrefix}-custom-flag-${flag.id}`} 
          flag={flag}
          flagTemplates={flagTemplates}
        />
      );
    });
  }

  return badges;
};

/**
 * Helper to render reservation badges with flex container
 * @param {Object} job - The job object
 * @param {string} className - Additional CSS classes for the container
 * @param {Array} flagTemplates - Optional array of flag templates for custom flags
 * @returns {JSX.Element} Reservation badges wrapped in a flex container
 */
export const renderReservationBadgesContainer = (job, className = "flex flex-wrap gap-2 mt-1", flagTemplates = []) => {
  const badges = renderReservationBadges(job, null, flagTemplates);
  
  // If no badges, show fallback message
  if (badges.length === 0) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        No reservation data
      </span>
    );
  }
  
  return (
    <div className={className}>
      {badges}
    </div>
  );
}; 